import React, { useEffect, useRef, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'
import { message, Popover } from 'antd'
import { debounce } from 'lodash'

import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import 'quill-mention/dist/quill.mention.css'
import { Mention } from 'quill-mention'
import AvatarMentionBlot, {
    getMentionInitials,
    getMentionAvatarColor,
    buildMentionPillElement,
    resolveProfilePictureUrl,
} from './MentionEditorMentionBlot'
import dayjs from 'dayjs'
import S3FileUpload from 'react-s3/lib/ReactS3'

// css
import './index.css'
import insertMenuStyles from './MentionEditorInsertMenu.module.css'
import assigneeStyles from '../../modules/projects/components/AssigneeSelector.module.css'

// components
import Button from '../button/button'
import MediaViewer from '../../modules/taskRefactored/components/mediaViewer'
import MentionEditorInsertMenu from './MentionEditorInsertMenu'
import {
    resolveAppLinkTitle,
    getLinkHref,
} from '../../modules/projects/components/DescriptionEditor/resolveAppLinkTitle'

// slices
import { loginState } from '../../modules/login/login.slice'
import {
    usersListForClientModal,
    usersState,
} from '../../modules/users/users.slice'

Quill.register('modules/mention', Mention)
Quill.register('formats/mention', AvatarMentionBlot)

const MENTION_HINT_ID = '__mention_hint__'
const MENTION_LOADING_MORE_ID = '__mention_loading_more__'
const MENTION_PAGE_LIMIT = 10

const looksLikePasteUrl = (text = '') => {
    const trimmed = String(text).trim()
    if (!trimmed || /\s/.test(trimmed)) return false
    // Require an explicit URL scheme or www. — bare words like "Description"
    // must not become relative links against window.location.origin.
    if (/^https?:\/\//i.test(trimmed)) return true
    if (/^www\./i.test(trimmed)) return true
    return false
}

const MentionEditor = ({
    member = [],
    projectId,
    excludeUserIds = [],
    handleChangeMentions,
    value,
    clearTrigger,
    loadingButton,
    onClickComment,
    disabledButton,
    getFileList,
    isEditMode = false,
    onCancelEdit,
    onSaveEdit,
    saveLoading = false,
    saveDisabled = false,
    showInsertMenu = false,
    disableMediaDrop = false,
    disableMentions = false,
}) => {
    const dispatch = useDispatch()
    const { userProfile } = useSelector(loginState)
    const { clientModalUsersListData = [] } = useSelector(usersState)
    const editorRef = useRef(null)
    const quillRef = useRef(null)
    const containerRef = useRef(null)
    const fileInputRef = useRef(null)
    const [showButton, setShowButton] = useState(false)
    const [hasBeenFocused, setHasBeenFocused] = useState(false)
    const [uploadedFiles, setUploadedFiles] = useState([])
    const [uploading, setUploading] = useState(false)
    const [editorReady, setEditorReady] = useState(false)
    const [linkPopup, setLinkPopup] = useState({
        open: false,
        text: '',
        url: '',
        savedRange: null,
    })
    const linkUrlInputRef = useRef(null)
    const linkTextInputRef = useRef(null)
    const isUnmountingRef = useRef(false)
    const initialValueRef = useRef(value)

    const projectIdRef = useRef(projectId)
    const excludeUserIdsRef = useRef(excludeUserIds)
    const memberRef = useRef(member)
    const userProfileRef = useRef(userProfile)
    const mentionUsersRef = useRef([])
    const mentionHasNextRef = useRef(false)
    const mentionSearchRef = useRef('')
    const mentionFetchLoadingRef = useRef(false)
    const mentionLoadMoreRef = useRef(false)
    const mentionRequestIdRef = useRef(0)
    const mentionListScrollRef = useRef(null)

    useEffect(() => {
        projectIdRef.current = projectId
    }, [projectId])

    useEffect(() => {
        excludeUserIdsRef.current = excludeUserIds
    }, [excludeUserIds])

    useEffect(() => {
        memberRef.current = member
    }, [member])

    useEffect(() => {
        userProfileRef.current = userProfile
    }, [userProfile])

    const mapApiUserToMention = (user) => ({
        id: user?._id,
        value: user?.name || user?.display || '',
        avatar: resolveProfilePictureUrl(
            user?.profilePicture || user?.image || user?.avatar
        ),
        email: user?.email,
    })

    const mapMemberToMention = (user) => ({
        id: user?.id || user?._id,
        value: user?.display || user?.name || '',
        avatar: resolveProfilePictureUrl(user?.profilePicture),
        email: user?.email,
    })

    const buildInsertMentionPayload = useCallback((datasetItem) => {
        const id = datasetItem?.id
        const fromList = mentionUsersRef.current.find(
            (u) => String(u.id) === String(id)
        )
        const avatar = resolveProfilePictureUrl(
            fromList?.avatar || datasetItem?.avatar
        )
        return {
            id: id || fromList?.id,
            value: (fromList?.value || datasetItem?.value || '').replace(
                /^@/,
                ''
            ),
            avatar,
            email: fromList?.email || datasetItem?.email || '',
            denotationChar: '',
        }
    }, [])

    const filterMentionUsers = useCallback((users) => {
        const exclude = new Set(
            [
                ...(excludeUserIdsRef.current || []),
                userProfileRef.current?._id,
            ].filter(Boolean)
        )
        return (users || []).filter((u) => u?.id && !exclude.has(u.id))
    }, [])

    const resolveMentionAvatar = useCallback(
        (id) => {
            if (!id) return ''
            const fromCache = mentionUsersRef.current.find((u) => u.id === id)
            if (fromCache?.avatar) return resolveProfilePictureUrl(fromCache.avatar)
            const fromModal = clientModalUsersListData.find((u) => u._id === id)
            if (fromModal?.profilePicture) {
                return resolveProfilePictureUrl(fromModal.profilePicture)
            }
            const fromMember = (memberRef.current || []).find(
                (m) => (m.id || m._id) === id
            )
            return resolveProfilePictureUrl(fromMember?.profilePicture)
        },
        [clientModalUsersListData]
    )

    const enhanceMentionNodesInEditor = useCallback(() => {
        const editor = editorRef.current?.querySelector('.ql-editor')
        if (!editor) return

        editor.querySelectorAll('span.mention').forEach((node) => {
            if (node.querySelector('.mention-editor-pill')) return

            const id = node.getAttribute('data-id')
            const rawValue =
                node.getAttribute('data-value') ||
                node.querySelector('.ql-mention-value')?.textContent ||
                ''
            const displayName = rawValue.replace(/^@/, '').trim()
            if (!displayName) return

            const avatar = resolveProfilePictureUrl(
                node.getAttribute('data-avatar') || resolveMentionAvatar(id)
            )

            node.querySelector('.ql-mention-denotation-char')?.remove()
            node.querySelector('.ql-mention-value')?.remove()
            node.innerHTML = ''
            node.contentEditable = 'false'
            node.appendChild(
                buildMentionPillElement({
                    id,
                    value: displayName,
                    avatar,
                })
            )
            AvatarMentionBlot.setDataValues(node, {
                id,
                value: displayName,
                avatar: avatar || undefined,
                denotationChar: '',
            })
        })
    }, [resolveMentionAvatar])

    const enhanceMentionNodesInEditorRef = useRef(enhanceMentionNodesInEditor)
    useEffect(() => {
        enhanceMentionNodesInEditorRef.current = enhanceMentionNodesInEditor
    }, [enhanceMentionNodesInEditor])

    const findMentionBlotAt = useCallback((quill, index) => {
        if (!quill || index == null) return null
        const [leaf] = quill.getLeaf(index)
        if (!leaf) return null
        let cur = leaf
        while (cur) {
            if (cur.statics?.blotName === 'mention') return cur
            cur = cur.parent
        }
        return null
    }, [])

    const moveCursorAfterMentionSync = useCallback(
        (quill, rangeIndex) => {
            if (!quill) return
            let idx =
                rangeIndex ??
                quill.getSelection()?.index ??
                quill.getLength() - 1

            const mentionBlot = findMentionBlotAt(quill, idx)
            if (mentionBlot) {
                idx = mentionBlot.offset(quill.scroll) + mentionBlot.length()
            }

            const nextChar = quill.getText(idx, 1)
            if (nextChar !== ' ') {
                quill.insertText(idx, ' ', 'user')
                idx += 1
            } else {
                idx += 1
            }

            quill.setSelection(idx, 0, 'user')
        },
        [findMentionBlotAt]
    )

    const placeCursorAfterMention = useCallback(() => {
        const quill = quillRef.current
        if (!quill) return
        requestAnimationFrame(() => {
            moveCursorAfterMentionSync(quill)
            quill.focus()
        })
    }, [moveCursorAfterMentionSync])

    const fetchMentionUsers = useCallback(
        async (search, skip, append) => {
            const pid = projectIdRef.current
            if (!pid) return []

            if (!append) {
                mentionFetchLoadingRef.current = true
            }

            try {
                const result = await dispatch(
                    usersListForClientModal({
                        skip,
                        limit: MENTION_PAGE_LIMIT,
                        search: search || '',
                        filter: {},
                        projectId: pid,
                    })
                ).unwrap()

                const mapped = filterMentionUsers(
                    (result?.data || []).map(mapApiUserToMention)
                )

                if (append) {
                    const seen = new Set(
                        mentionUsersRef.current.map((u) => u.id)
                    )
                    mentionUsersRef.current = [
                        ...mentionUsersRef.current,
                        ...mapped.filter((u) => !seen.has(u.id)),
                    ]
                } else {
                    mentionUsersRef.current = mapped
                }

                mentionHasNextRef.current = Boolean(result?.isNext)
                mentionSearchRef.current = search
                return mentionUsersRef.current
            } catch {
                if (!append) {
                    mentionUsersRef.current = []
                }
                mentionHasNextRef.current = false
                return mentionUsersRef.current
            } finally {
                if (!append) {
                    mentionFetchLoadingRef.current = false
                }
            }
        },
        [dispatch, filterMentionUsers]
    )

    const getMentionListForRender = useCallback(() => {
        const items = [...mentionUsersRef.current]
        if (mentionHasNextRef.current && mentionLoadMoreRef.current) {
            items.push({
                id: MENTION_LOADING_MORE_ID,
                value: '',
                disabled: true,
            })
        }
        return items
    }, [])

    const refreshMentionDropdown = useCallback(() => {
        const mentionMod = quillRef.current?.getModule('mention')
        if (!mentionMod || !mentionSearchRef.current) return
        mentionMod.renderList(
            '@',
            getMentionListForRender(),
            mentionSearchRef.current
        )
    }, [getMentionListForRender])

    const handleMentionListScroll = useCallback(
        async (e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target
            if (scrollHeight - scrollTop > clientHeight + 40) return
            if (!projectIdRef.current || !mentionHasNextRef.current) return
            if (mentionLoadMoreRef.current || mentionFetchLoadingRef.current) {
                return
            }

            mentionLoadMoreRef.current = true
            refreshMentionDropdown()

            const prevLen = mentionUsersRef.current.length
            try {
                await fetchMentionUsers(mentionSearchRef.current, prevLen, true)
            } finally {
                mentionLoadMoreRef.current = false
                refreshMentionDropdown()
            }
        },
        [fetchMentionUsers, refreshMentionDropdown]
    )

    const attachMentionListScroll = useCallback(() => {
        const list =
            document.querySelector(`.${assigneeStyles.assigneeSelectorList}`) ||
            document.querySelector('.ql-mention-list')

        if (!list || mentionListScrollRef.current === list) return

        if (mentionListScrollRef.current) {
            mentionListScrollRef.current.removeEventListener(
                'scroll',
                handleMentionListScroll
            )
        }

        list.addEventListener('scroll', handleMentionListScroll)
        mentionListScrollRef.current = list
    }, [handleMentionListScroll])

    const detachMentionListScroll = useCallback(() => {
        if (mentionListScrollRef.current) {
            mentionListScrollRef.current.removeEventListener(
                'scroll',
                handleMentionListScroll
            )
            mentionListScrollRef.current = null
        }
    }, [handleMentionListScroll])

    const debouncedRemoteSearchRef = useRef(
        debounce(async (searchTerm, renderList) => {
            const query = (searchTerm ?? '').trim()
            if (!query || !projectIdRef.current) return

            const requestId = ++mentionRequestIdRef.current
            await fetchMentionUsers(query, 0, false)

            if (requestId !== mentionRequestIdRef.current) return
            renderList(getMentionListForRender(), searchTerm)
        }, 300)
    )

    const renderMentionItem = useCallback((item) => {
        if (item?.id === MENTION_HINT_ID) {
            const hint = document.createElement('div')
            hint.className = 'mention-hint-item'
            hint.textContent =
                'Mention a teammate or link to a task, project, or message.'
            return hint
        }

        if (item?.id === MENTION_LOADING_MORE_ID) {
            const loading = document.createElement('motion.div')
            loading.className = assigneeStyles.assigneeSelectorLoadingMore
            loading.innerHTML =
                '<span style="display:inline-flex;color:#6366f1"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2a10 10 0 1 0 10 10" opacity="0.3"/><path fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8z"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></path></svg></span>'
            return loading
        }

        const container = document.createElement('div')
        container.className = assigneeStyles.assigneeSelectorItem

        const avatarWrapper = document.createElement('span')
        avatarWrapper.style.cssText = [
            'width:26px',
            'height:26px',
            'min-width:26px',
            'max-width:26px',
            'min-height:26px',
            'max-height:26px',
            'border-radius:50%',
            'overflow:hidden',
            'display:inline-flex',
            'align-items:center',
            'justify-content:center',
            'flex-shrink:0',
            'font-size:14px',
            'font-weight:600',
            'color:#ffffff',
            `background-color:${getMentionAvatarColor(item?.value)}`,
        ].join(';')

        const avatarUrl = resolveProfilePictureUrl(
            item?.avatar || item?.profilePicture
        )
        if (avatarUrl) {
            const img = document.createElement('img')
            img.src = avatarUrl
            img.alt = item?.value || ''
            img.width = 26
            img.height = 26
            img.style.cssText =
                'width:26px;height:26px;min-width:26px;max-width:26px;min-height:26px;max-height:26px;object-fit:cover;display:block;'
            avatarWrapper.appendChild(img)
        } else {
            avatarWrapper.textContent = getMentionInitials(item?.value)
        }

        const info = document.createElement('div')
        info.className = assigneeStyles.assigneeSelectorItemInfo
        info.style.flexDirection = 'column'
        info.style.alignItems = 'flex-start'
        info.style.gap = '2px'

        const name = document.createElement('span')
        name.className = assigneeStyles.assigneeSelectorName
        name.textContent = item?.value || ''

        info.appendChild(name)

        if (item?.email) {
            const email = document.createElement('span')
            email.className = assigneeStyles.assigneeSelectorEmail
            email.textContent = item.email
            info.appendChild(email)
        }

        container.appendChild(avatarWrapper)
        container.appendChild(info)
        return container
    }, [])

    useEffect(() => {
        isUnmountingRef.current = false
        return () => {
            isUnmountingRef.current = true
        }
    }, [])

    useEffect(() => {
        if (!editorRef.current || quillRef.current) return
        if (!projectId && member.length === 0) return

        quillRef.current = new Quill(editorRef.current, {
            theme: 'snow',
            placeholder: 'Add a comment',
            modules: {
                toolbar: false,
                ...(disableMentions
                    ? {}
                    : {
                          mention: {
                              mentionDenotationChars: ['@'],
                              showDenotationChar: false,
                              positioningStrategy: 'fixed',
                              defaultMenuOrientation: 'bottom',
                              dataAttributes: [
                                  'id',
                                  'value',
                                  'denotationChar',
                                  'avatar',
                                  'email',
                                  'link',
                                  'target',
                                  'disabled',
                              ],
                              isolateCharacter: true,
                              spaceAfterInsert: true,
                              minChars: 0,
                              onSelect: (datasetItem, insertItem) => {
                                  if (
                                      datasetItem?.disabled ||
                                      datasetItem?.id === MENTION_HINT_ID ||
                                      datasetItem?.id === MENTION_LOADING_MORE_ID
                                  ) {
                                      return
                                  }
                                  insertItem(
                                      buildInsertMentionPayload(datasetItem)
                                  )
                                  placeCursorAfterMention()
                                  requestAnimationFrame(() => {
                                      enhanceMentionNodesInEditorRef.current?.()
                                  })
                              },
                              mentionContainerClass: `ql-mention-list-container ${assigneeStyles.assigneeSelectorPopup}`,
                              mentionListClass: `ql-mention-list ${assigneeStyles.assigneeSelectorList}`,
                              listItemClass: 'ql-mention-list-item',
                              renderItem: renderMentionItem,
                              renderLoading: () => {
                                  const loading = document.createElement('div')
                                  loading.className =
                                      assigneeStyles.assigneeSelectorLoading
                                  loading.innerHTML =
                                      '<span style="display:inline-flex;color:#6366f1"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2a10 10 0 1 0 10 10" opacity="0.3"/><path fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8z"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></path></svg></span>'
                                  return loading
                              },
                              onOpen: () => {
                                  setTimeout(() => attachMentionListScroll(), 0)
                              },
                              onBeforeClose: () => {
                                  detachMentionListScroll()
                              },
                              source: (searchTerm, renderList) => {
                                  const query = (searchTerm ?? '').trim()
                                  if (!query) {
                                      renderList(
                                          [
                                              {
                                                  id: MENTION_HINT_ID,
                                                  value: '',
                                                  disabled: true,
                                              },
                                          ],
                                          searchTerm
                                      )
                                      return
                                  }

                                  if (projectIdRef.current) {
                                      mentionRequestIdRef.current += 1
                                      debouncedRemoteSearchRef.current.cancel()
                                      debouncedRemoteSearchRef.current(
                                          searchTerm,
                                          renderList
                                      )
                                      return
                                  }

                                  const q = query.toLowerCase()
                                  const matches = filterMentionUsers(
                                      (memberRef.current || [])
                                          .map(mapMemberToMention)
                                          .filter((item) => {
                                              const name = (
                                                  item.value || ''
                                              ).toLowerCase()
                                              const email = (
                                                  item.email || ''
                                              ).toLowerCase()
                                              return (
                                                  name.includes(q) ||
                                                  email.includes(q)
                                              )
                                          })
                                  )
                                  renderList(matches, searchTerm)
                              },
                          },
                      }),
            },
        })

        // Set placeholder data attribute for CSS
        const editorElement = editorRef.current.querySelector('.ql-editor')
        if (editorElement) {
            editorElement.setAttribute('data-placeholder', 'Add a comment')
        }

        const initialHtml = initialValueRef.current
        if (initialHtml && typeof initialHtml === 'string') {
            quillRef.current.clipboard.dangerouslyPasteHTML(initialHtml)
            requestAnimationFrame(() => enhanceMentionNodesInEditor())
        }

        quillRef.current.on('selection-change', (range) => {
            if (!range || range.length > 0) return
            const quill = quillRef.current
            if (!quill || !findMentionBlotAt(quill, range.index)) return
            moveCursorAfterMentionSync(quill, range.index)
        })

        quillRef.current.on('text-change', () => {
            enhanceMentionNodesInEditorRef.current?.()

            const html =
                editorRef.current.querySelector('.ql-editor')?.innerHTML || ''
            const delta = quillRef.current.getContents()
            const mentions = []
            let plainText = ''

            delta.ops?.forEach((op) => {
                if (typeof op.insert === 'string') {
                    plainText += op.insert
                } else if (op.insert?.mention) {
                    mentions.push(op.insert.mention.id)
                    const name = (op.insert.mention.value || '').replace(
                        /^@/,
                        ''
                    )
                    plainText += name ? `${name} ` : ''
                }
            })

            handleChangeMentions(html, plainText.trim(), [...new Set(mentions)])

            // 👇 toggle button only if user typed something
            const hasContent =
                quillRef.current.getText().trim().length > 0 ||
                mentions.length > 0
            setShowButton(hasContent)
        })

        setEditorReady(true)
    }, [
        member,
        projectId,
        disableMentions,
        handleChangeMentions,
        renderMentionItem,
        attachMentionListScroll,
        detachMentionListScroll,
        filterMentionUsers,
        enhanceMentionNodesInEditor,
        placeCursorAfterMention,
        findMentionBlotAt,
        moveCursorAfterMentionSync,
        buildInsertMentionPayload,
    ])

    // Keep mention menu portaled even if Quill was created before this option existed.
    useEffect(() => {
        if (!editorReady) return
        const mention = quillRef.current?.getModule('mention')
        if (!mention?.options) return
        mention.options.positioningStrategy = 'fixed'
        mention.options.defaultMenuOrientation = 'bottom'
    }, [editorReady])

    // Paste / link-click / @-after-mention: keep listeners attached even when
    // parent re-renders and updates `value` (init effect early-returns).
    useEffect(() => {
        if (!editorReady) return
        const quill = quillRef.current
        if (!quill?.root) return

        const editorElement = quill.root

        const handlePaste = (e) => {
            const plain = e.clipboardData?.getData('text/plain') || ''
            if (!looksLikePasteUrl(plain)) return

            e.preventDefault()
            e.stopPropagation()

            const editor = quillRef.current
            if (!editor) return

            const pasted = plain.trim()
            const urlCandidate = /^www\./i.test(pasted)
                ? `https://${pasted}`
                : pasted
            let href
            try {
                href = new URL(urlCandidate).href
            } catch {
                href = urlCandidate
            }

            const range = editor.getSelection(true) ?? {
                index: Math.max(editor.getLength() - 1, 0),
                length: 0,
            }
            if (range.length) {
                editor.deleteText(range.index, range.length, 'user')
            }

            const insertIndex = range.index
            editor.insertText(insertIndex, pasted, 'link', href, 'user')
            editor.setSelection(insertIndex + pasted.length, 0, 'silent')

            resolveAppLinkTitle(pasted).then((resolved) => {
                if (isUnmountingRef.current || !resolved?.title) return
                const q = quillRef.current
                if (!q || q !== editor) return
                const current = q.getText(insertIndex, pasted.length)
                if (current !== pasted) return
                const resolvedHref = resolved.href || href
                q.deleteText(insertIndex, pasted.length, 'user')
                q.insertText(
                    insertIndex,
                    resolved.title,
                    'link',
                    resolvedHref,
                    'user'
                )
                q.setSelection(
                    insertIndex + resolved.title.length,
                    0,
                    'silent'
                )
            })
        }

        const handleLinkClick = (e) => {
            const anchor = e.target?.closest?.('a[href]')
            if (!anchor || !editorElement.contains(anchor)) return
            const href = anchor.getAttribute('href')
            if (!href || href === '#') return
            e.preventDefault()
            e.stopPropagation()
            window.open(href, '_blank', 'noopener,noreferrer')
        }

        const handleEditorKeyDown = (e) => {
            if (e.key !== '@') return
            const editor = quillRef.current
            if (!editor) return
            const range = editor.getSelection()
            if (range == null) return
            const mentionBlot = findMentionBlotAt(editor, range.index)
            if (!mentionBlot) return

            e.preventDefault()
            let idx = mentionBlot.offset(editor.scroll) + mentionBlot.length()
            const nextChar = editor.getText(idx, 1)
            if (nextChar !== ' ') {
                editor.insertText(idx, ' ', 'user')
                idx += 1
            } else {
                idx += 1
            }
            editor.insertText(idx, '@', 'user')
            editor.setSelection(idx + 1, 0, 'user')
        }

        editorElement.addEventListener('paste', handlePaste, true)
        editorElement.addEventListener('click', handleLinkClick)
        editorElement.addEventListener('keydown', handleEditorKeyDown)

        return () => {
            editorElement.removeEventListener('paste', handlePaste, true)
            editorElement.removeEventListener('click', handleLinkClick)
            editorElement.removeEventListener('keydown', handleEditorKeyDown)
        }
    }, [editorReady, findMentionBlotAt])

    // Story map comments: block image/file drag-drop and image paste into the editor.
    useEffect(() => {
        if (!editorReady || !disableMediaDrop) return
        const quill = quillRef.current
        if (!quill?.root) return

        const editorElement = quill.root

        const hasFiles = (dataTransfer) => {
            if (!dataTransfer) return false
            if (dataTransfer.files?.length > 0) return true
            const types = Array.from(dataTransfer.types || [])
            return types.includes('Files')
        }

        const blockDrop = (e) => {
            if (!hasFiles(e.dataTransfer)) return
            e.preventDefault()
            e.stopPropagation()
        }

        const blockPasteFiles = (e) => {
            const items = Array.from(e.clipboardData?.items || [])
            const hasMedia = items.some(
                (item) =>
                    item.kind === 'file' ||
                    String(item.type || '').startsWith('image/')
            )
            if (!hasMedia) return
            e.preventDefault()
            e.stopPropagation()
        }

        quill.clipboard.addMatcher('IMG', (node, delta) => {
            delta.ops = []
            return delta
        })

        editorElement.addEventListener('dragenter', blockDrop, true)
        editorElement.addEventListener('dragover', blockDrop, true)
        editorElement.addEventListener('drop', blockDrop, true)
        editorElement.addEventListener('paste', blockPasteFiles, true)

        const removeEmbeddedImages = () => {
            const editor = quillRef.current
            if (!editor) return
            const imgs = Array.from(editor.root.querySelectorAll('img')).filter(
                (img) =>
                    !img.closest(
                        '.mention-editor-pill-avatar, .mention, .ql-mention'
                    )
            )
            if (!imgs.length) return
            imgs.forEach((img) => img.remove())
        }

        quill.on('text-change', removeEmbeddedImages)

        return () => {
            editorElement.removeEventListener('dragenter', blockDrop, true)
            editorElement.removeEventListener('dragover', blockDrop, true)
            editorElement.removeEventListener('drop', blockDrop, true)
            editorElement.removeEventListener('paste', blockPasteFiles, true)
            quill.off('text-change', removeEmbeddedImages)
        }
    }, [editorReady, disableMediaDrop])

    useEffect(() => {
        const debouncedSearch = debouncedRemoteSearchRef.current
        return () => {
            debouncedSearch?.cancel()
            detachMentionListScroll()
        }
    }, [detachMentionListScroll])

    // external clear trigger
    useEffect(() => {
        if (clearTrigger && quillRef.current) {
            quillRef.current.setText('')
            setShowButton(false)
            // Clear uploaded files when editor is cleared
            setUploadedFiles([])
            if (getFileList) {
                getFileList([])
            }
        }
    }, [clearTrigger, getFileList])

    const handleFocus = () => {
        containerRef.current?.classList.add('focused')
        setHasBeenFocused(true)
    }
    const handleBlur = () => {
        containerRef.current?.classList.remove('focused')
        // Keep the editor expanded if it has been focused before
        if (hasBeenFocused) {
            containerRef.current?.classList.add('expanded')
        }
    }

    // 👇 Clear editor on submit
    const handleSubmit = () => {
        onClickComment()
        if (quillRef.current) {
            quillRef.current.setText('') // clear text
            setShowButton(false) // hide button again
        }
    }

    // Handle file selection
    const handleFileSelect = () => {
        fileInputRef.current?.click()
    }

    // Handle file upload using S3FileUpload
    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files || [])
        if (files.length === 0) return

        // Check if s3Config exists
        if (!userProfile?.s3Config) {
            message.error('S3 configuration not available. Please try again.')
            return
        }

        setUploading(true)
        const newFileUrls = []
        let completedCount = 0
        const totalFiles = files.length

        files.forEach((file) => {
            try {
                const nameChange = file.name
                    .split('.')[0]
                    .concat(`_${dayjs(new Date()).unix()}`)
                    .concat(`.${file.name.split('.').pop()}`)

                // Create new File object with the file's content
                // Since file is already a File object, we can slice it to get a new one
                const newFileData = new File([file], nameChange, {
                    type: file.type,
                    lastModified: Date.now(),
                })

                window.Buffer = window.Buffer || require('buffer').Buffer

                S3FileUpload.uploadFile(newFileData, userProfile?.s3Config)
                    .then(async (data) => {
                        completedCount++
                        if (data?.location) {
                            newFileUrls.push(data.location)
                        }

                        // When all files are uploaded
                        if (completedCount === totalFiles) {
                            // Update state with all new files
                            if (newFileUrls.length > 0) {
                                setUploadedFiles((prev) => {
                                    const updated = [...prev, ...newFileUrls]
                                    // Notify parent component with all files
                                    if (getFileList) {
                                        getFileList(updated)
                                    }
                                    return updated
                                })
                            }
                            setUploading(false)
                        }
                    })
                    .catch((error) => {
                        completedCount++
                        console.error('Upload error:', error)
                        message.error(
                            error?.message || 'Upload Failed! Please try again'
                        )
                        if (completedCount === totalFiles) {
                            setUploading(false)
                        }
                    })
            } catch (error) {
                completedCount++
                console.error('File processing error:', error)
                message.error('Upload Failed! Please try again')
                if (completedCount === totalFiles) {
                    setUploading(false)
                }
            }
        })

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    // Handle file delete
    const handleFileDelete = (fileUrl) => {
        const updatedFiles = uploadedFiles.filter((url) => url !== fileUrl)
        setUploadedFiles(updatedFiles)

        // Notify parent component
        if (getFileList) {
            getFileList(updatedFiles)
        }
    }

    // Formatting handlers
    const handleFormat = (format) => {
        if (quillRef.current) {
            const range = quillRef.current.getSelection(true)
            if (range) {
                const formatValue = quillRef.current.getFormat(range)
                const isActive = formatValue[format]
                quillRef.current.format(format, !isActive)
            }
        }
    }

    const handleBold = () => handleFormat('bold')
    const handleItalic = () => handleFormat('italic')
    const handleUnderline = () => handleFormat('underline')

    const closeLinkPopup = useCallback(() => {
        setLinkPopup({ open: false, text: '', url: '', savedRange: null })
        quillRef.current?.focus()
    }, [])

    const openLinkPopup = useCallback(() => {
        const q = quillRef.current
        if (!q) return
        q.focus()
        const range = q.getSelection(true) ?? {
            index: Math.max(q.getLength() - 1, 0),
            length: 0,
        }
        const fmt = range.length ? q.getFormat(range) : q.getFormat(range.index)
        const selectedText = range.length
            ? q.getText(range.index, range.length)
            : ''
        setLinkPopup({
            open: true,
            text: selectedText,
            url: getLinkHref(fmt?.link) || '',
            savedRange: range,
        })
    }, [])

    const handleLinkSubmit = useCallback(() => {
        const q = quillRef.current
        if (!q) return
        const { text, url, savedRange } = linkPopup
        if (!url.trim()) {
            if (savedRange?.length) {
                q.setSelection(savedRange.index, savedRange.length, 'silent')
                q.format('link', false)
            }
            closeLinkPopup()
            return
        }
        const href = url.trim()
        if (savedRange?.length) {
            q.setSelection(savedRange.index, savedRange.length, 'silent')
            q.format('link', href)
        } else {
            const insertText = text.trim() || href
            const idx = savedRange?.index ?? Math.max(q.getLength() - 1, 0)
            q.insertText(idx, insertText, 'link', href, 'user')
            q.setSelection(idx + insertText.length, 0)
        }
        closeLinkPopup()
    }, [linkPopup, closeLinkPopup])

    const handleLinkKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                handleLinkSubmit()
            }
            if (e.key === 'Escape') {
                e.preventDefault()
                closeLinkPopup()
            }
        },
        [handleLinkSubmit, closeLinkPopup]
    )

    useEffect(() => {
        if (!linkPopup.open) return
        const focusTarget = linkPopup.text
            ? linkUrlInputRef.current
            : linkTextInputRef.current
        const timer = setTimeout(() => focusTarget?.focus(), 0)
        return () => clearTimeout(timer)
    }, [linkPopup.open]) // eslint-disable-line react-hooks/exhaustive-deps

    const linkPopoverContent = (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <input
                ref={linkTextInputRef}
                className={insertMenuStyles.linkInput}
                value={linkPopup.text}
                onChange={(e) =>
                    setLinkPopup((s) => ({ ...s, text: e.target.value }))
                }
                onKeyDown={handleLinkKeyDown}
                placeholder="Text"
                type="text"
            />
            <input
                ref={linkUrlInputRef}
                className={insertMenuStyles.linkInput}
                value={linkPopup.url}
                onChange={(e) =>
                    setLinkPopup((s) => ({ ...s, url: e.target.value }))
                }
                onKeyDown={handleLinkKeyDown}
                placeholder="URL"
                type="url"
            />
            <div className={insertMenuStyles.linkActions}>
                <button
                    type="button"
                    className={insertMenuStyles.linkCancelBtn}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        closeLinkPopup()
                    }}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className={insertMenuStyles.linkSubmitBtn}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        handleLinkSubmit()
                    }}
                >
                    Apply
                </button>
            </div>
        </div>
    )

    return (
        <>
            <div ref={containerRef} className="mention-editor-container">
                <div
                    ref={editorRef}
                    className="mention-editor"
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
                {(hasBeenFocused || showButton) && (
                    <div className="mention-editor-actions">
                        {hasBeenFocused && (
                            <div className="mention-editor-formatting">
                                {showInsertMenu && (
                                    <MentionEditorInsertMenu
                                        quillRef={quillRef}
                                    />
                                )}
                                <Button
                                    props={{
                                        icon: (
                                            <Icon
                                                icon="octicon:bold-16"
                                                width={16}
                                                height={16}
                                            />
                                        ),
                                        buttonType: 'link',
                                        onClick: handleBold,
                                        style: {
                                            color: '#64748b',
                                            padding: 0,
                                        },
                                    }}
                                />
                                <Button
                                    props={{
                                        icon: (
                                            <Icon
                                                icon="si:italic-fill"
                                                width={16}
                                                height={16}
                                            />
                                        ),
                                        buttonType: 'link',
                                        onClick: handleItalic,
                                        style: {
                                            color: '#64748b',
                                            padding: 0,
                                        },
                                    }}
                                />
                                <Button
                                    props={{
                                        icon: (
                                            <Icon
                                                icon="solar:text-underline-linear"
                                                width={16}
                                                height={16}
                                            />
                                        ),
                                        buttonType: 'link',
                                        onClick: handleUnderline,
                                        style: {
                                            color: '#64748b',
                                            padding: 0,
                                        },
                                    }}
                                />
                                <Popover
                                    open={linkPopup.open}
                                    onOpenChange={(open) => {
                                        if (!open) closeLinkPopup()
                                    }}
                                    trigger={[]}
                                    placement="topLeft"
                                    overlayClassName={insertMenuStyles.linkPopover}
                                    getPopupContainer={() => document.body}
                                    content={linkPopoverContent}
                                    destroyOnHidden
                                >
                                    <span>
                                        <Button
                                            props={{
                                                icon: (
                                                    <Icon
                                                        icon="ri:link"
                                                        width={16}
                                                        height={16}
                                                    />
                                                ),
                                                buttonType: 'link',
                                                onClick: openLinkPopup,
                                                onMouseDown: (e) =>
                                                    e.preventDefault(),
                                                title: 'Insert link',
                                                style: {
                                                    color: '#64748b',
                                                    padding: 0,
                                                },
                                            }}
                                        />
                                    </span>
                                </Popover>
                                {getFileList && (
                                    <Button
                                        props={{
                                            icon: (
                                                <Icon
                                                    icon="ic:baseline-attach-file"
                                                    width={16}
                                                    height={16}
                                                />
                                            ),
                                            buttonType: 'link',
                                            type: 'files',
                                            onClick: handleFileSelect,
                                            style: {
                                                letterSpacing: '0.7px',
                                                color: '#64748b',
                                                padding: 0,
                                            },
                                        }}
                                    />
                                )}
                            </div>
                        )}

                        <div className="mention-editor-right-actions">
                            {isEditMode ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: '8px',
                                    }}
                                >
                                    <Button
                                        props={{
                                            text: 'Cancel',
                                            onClick: onCancelEdit,
                                            style: {
                                                height: '36px',
                                                padding: '0 14px',
                                                borderRadius: '8px',
                                                fontWeight: 500,
                                                fontSize: '14px',
                                                border: '1px solid #e2e8f0',
                                                background: '#ffffff',
                                                color: '#475569',
                                            },
                                        }}
                                    />
                                    <Button
                                        props={{
                                            text: 'Save changes',
                                            onClick: onSaveEdit,
                                            loadingButton: saveLoading,
                                            disabled: saveDisabled,
                                            style: {
                                                height: '36px',
                                                padding: '0 14px',
                                                borderRadius: '8px',
                                                fontWeight: 500,
                                                fontSize: '14px',
                                                backgroundColor: '#4F46E5',
                                                border: 'none',
                                                color: '#fff',
                                            },
                                        }}
                                    />
                                </div>
                            ) : (
                                showButton && (
                                    <div className="comment-button-wrapper">
                                        <Button
                                            props={{
                                                text: 'Comment',
                                                style: {
                                                    margin: 0,
                                                    height: '36px',
                                                    padding: '0 14px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#4F46E5',
                                                    border: 'none',
                                                    color: '#fff',
                                                    fontWeight: 400,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 14,
                                                },
                                                loadingButton: loadingButton,
                                                onClick: handleSubmit,
                                                disabled: disabledButton,
                                            }}
                                        />
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Hidden file input */}
            {getFileList && (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        accept="*"
                    />

                    {/* Display uploaded files */}
                    {uploadedFiles.length > 0 && (
                        <div
                            className="mention-editor-attachments"
                            style={{
                                marginTop: '12px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '10px',
                                maxWidth: '100%',
                                overflow: 'hidden',
                            }}
                        >
                            <MediaViewer
                                mediaFiles={uploadedFiles}
                                handleDelete={handleFileDelete}
                            />
                        </div>
                    )}

                    {/* Uploading indicator */}
                    {uploading && (
                        <div
                            style={{
                                marginTop: '8px',
                                color: '#64748b',
                                fontSize: '12px',
                            }}
                        >
                            Uploading files...
                        </div>
                    )}
                </>
            )}
        </>
    )
}

export default MentionEditor
